import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';

import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { AccountResponseDTO, IsActiveEnum } from '_lib/api/apiTypes.shim';
import { Avatar } from '_lib/components/Avatar/Avatar';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { LinkListItemButton } from '_lib/components/LinkListItemButton/LinkListItemButton';
import { List } from '_lib/components/List/List';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ListItemAvatar } from '_lib/components/ListItemAvatar/ListItemAvatar';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { Tab } from '_lib/components/Tab/Tab';
import { Tabs } from '_lib/components/Tabs/Tabs';

import { Tooltip } from '_lib/components/Tooltip/Tooltip';

import { Typography } from '_lib/components/Typography/Typography';
import { AuthContext } from '_lib/featureComponents/auth/authContext';
import { useCompanyCreateDialog } from '_lib/featureComponents/Company/CompanyCreate/hooks/CompanyCreateDialogHook';
import { appRoutePaths, getFullAppRoutePath } from '_lib/featureComponents/Nav/nav.types';
import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';
import { formatAddressOneLine } from '_lib/utils/helpers';

import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { useDeactivateLastCompanyDialog } from '../Account/hooks/DeactivateLastCompanyDialogHook';

type AccountCompanyTabsProps = {
  account: AccountResponseDTO;
};

export const AccountCompanyTabs = (props: AccountCompanyTabsProps) => {
  const { account } = props;
  const [tabIndex, setTabIndex] = useState(0);

  const api = useContext(ApiContext);
  const { authUserCacheResource, callCenterCacheResource } = useContext(AuthContext);
  const authUserQuery = authUserCacheResource.useCacheQuery();
  const callCenterQuery = callCenterCacheResource.useCacheQuery();

  const navigate = useNavigate();

  const accountCompanyDeleteMutation = api.companies.useDelete();
  const accountCompanyReactivateMutation = api.companies.useReactivate();

  const confirmationDialog = useConfirmationDialog();
  const deactivateLastCompanyDialog = useDeactivateLastCompanyDialog();
  const createCompanyDialog = useCompanyCreateDialog();
  const { enqueueSnackbar } = useOurSnackbar();

  const accountCompaniesQuery = api.companies.useUnpagedListFull({
    params: {
      accountId: account.id,
      serializer: 'full',
      isActive: tabIndex === 0 ? IsActiveEnum.True : IsActiveEnum.False
    }
  });

  return (
    <FlexBox
      sx={{
        flexDirection: 'column',
        maxHeight: '400px',
        width: '100%',
        overflow: 'hidden',
        position: 'relative'
      }}>
      <Tabs value={tabIndex} onChange={(_event, newValue: number) => setTabIndex(newValue)}>
        <Tab key={0} label='ACTIVE' />
        <Tab key={1} label='INACTIVE' />
      </Tabs>
      <Divider />
      <FlexBox
        sx={{
          minHeight: '72px',
          position: 'relative',
          flexDirection: 'column',
          overflow: 'auto'
        }}>
        <BackdropSpinner
          sx={{ alignSelf: 'baseline', paddingTop: 2 }}
          open={
            accountCompaniesQuery.isFetching ||
            accountCompanyDeleteMutation.isPending ||
            accountCompanyReactivateMutation.isPending
          }
        />
        {!accountCompaniesQuery.isFetching && !accountCompaniesQuery.data?.length && (
          <FlexBox sx={{ justifyContent: 'center' }}>
            <Typography variant='subtitle1' text='No Account Companies Found' sx={{ paddingTop: 3 }} />
          </FlexBox>
        )}
        {!accountCompaniesQuery.isFetching && !!accountCompaniesQuery.data?.length && (
          <List sx={{ padding: 0 }}>
            {accountCompaniesQuery.data.map((accountCompany) => (
              <ListItem
                key={accountCompany.id}
                divider
                disablePadding
                secondaryAction={
                  <Tooltip title={`${accountCompany.isActive ? 'Deactivate' : 'Reactivate'} Company`} placement='left'>
                    <IconButton
                      disabled={account.isActive === false}
                      onClick={async () => {
                        if (accountCompaniesQuery.data.length === 1 && accountCompany.isActive) {
                          await deactivateLastCompanyDialog.show({ accountId: account.id, company: accountCompany });
                          return;
                        }

                        const result = await confirmationDialog.show({
                          titleText: `${accountCompany.isActive ? 'Deactivate' : 'Reactivate'} Company?`,
                          contentText: ''
                        });
                        if (result?.action === 'CANCEL') return;

                        try {
                          if (accountCompany.isActive) {
                            await accountCompanyDeleteMutation.deleteItem({ id: accountCompany.id });
                          } else {
                            await accountCompanyReactivateMutation.reactivate({ id: accountCompany.id });
                          }
                          enqueueSnackbar(`Company ${accountCompany.isActive ? 'deactivated' : 'reactivated'}`);
                        } catch (err) {
                          enqueueSnackbar(
                            `Company could not be ${accountCompany.isActive ? 'deactivated' : 'reactivated'}`
                          );
                          logDebug(err);
                        }
                      }}>
                      {accountCompany.isActive ? <DeleteIcon /> : <SettingsBackupRestoreIcon />}
                    </IconButton>
                  </Tooltip>
                }>
                <LinkListItemButton
                  href={`${getFullAppRoutePath(appRoutePaths.Company)}/${accountCompany.id}`}
                  onClick={() => {
                    navigate(`${getFullAppRoutePath(appRoutePaths.Company)}/${accountCompany.id}`);
                  }}>
                  <ListItemAvatar>
                    <Avatar>{<BusinessIcon />}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={accountCompany.name}
                    secondary={formatAddressOneLine(accountCompany, { includeCountry: true })}
                  />
                </LinkListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </FlexBox>
      <Divider />
      <FlexBox sx={{ width: '100%', height: '48px', justifyContent: 'flex-end' }}>
        <Tooltip title={account.isActive === false ? 'Cannot add company to an inactive account' : ''}>
          <FlexBox sx={{ marginY: 1, marginRight: 1 }}>
            <Button
              startIcon={<AddCircleOutlineIcon />}
              disabled={account.isActive === false || !authUserQuery.data || !callCenterQuery.data}
              text='Company'
              variant='contained'
              sx={{ textTransform: 'none' }}
              onClick={async () => {
                if (!authUserQuery.data || !callCenterQuery.data) return;

                const result = await createCompanyDialog.show({
                  account,
                  callCenter: callCenterQuery.data,
                  authUser: authUserQuery.data
                });
                if (result?.action === 'CONFIRM' && result.value) {
                  navigate(`${getFullAppRoutePath(appRoutePaths.Company)}/${result.value.createdCompany.id}`);
                }
              }}
            />
          </FlexBox>
        </Tooltip>
      </FlexBox>
      {confirmationDialog.element}
      {deactivateLastCompanyDialog.element}
      {createCompanyDialog.element}
    </FlexBox>
  );
};
